import React, { useEffect } from 'react';
import { Col, Row } from '@gjensidige/core-grid';
import {
  fetchGenericIllustrations,
  IllustrationType
} from '@gjensidige/se-content-helpers';
import {
  apiCallBegan,
  APICallMethods
} from '@gjensidige/se-redux-api-middleware';
import { PriceBanner } from '@gjensidige/se-price-banner';

import { useAppDispatch, useAppSelector } from '../hooks/appHooks';
import { ROUTES } from '../lib/constants';
import APP_CONSTANTS, { API_OPERATIONS, SESSION_KEYS } from '../lib/constants/appConstants';
import API_ENDPOINTS from '../lib/constants/endpoints';
import {
  onApiBegan,
  onApiError,
  onApiSuccess,
  updateLoadingStatus,
  updatePurchaseStatus
} from '../store/slices/offerSlice';
import tracker from '../tracking';
import { ErrorPageHandler } from '../components/Common/ErrorPage/ErrorPageHandler';
import { PageLoader } from '../components/Common/Loader/PageLoader';
import { SectionLoader } from '../components/Common/Loader/SectionLoader';
import { PropertyDataModal } from '../components/PropertyData/PropertyDataModal';
import { HeaderContainer } from '../containers/HeaderContainer';
import { MoreInputsContainer } from '../containers/MoreInputsContainer';
import { WelcomeBackModal } from '../components/Summary/WelcomeBackModal';
import { Package } from '../interfaces/Types';
import { AppConstants } from '../utils/AppConstants';
import { InsuranceHelper } from '../utils/InsuranceHelper';
import { TextHelper } from '../utils/TextHelper';
import { TrackingHelper } from '../utils/TrackingHelper';
import { logMessage } from '../utils/loggers';

interface MoreInputsPageProps {
  product: string;
}
export const MoreInputsPage: React.FC<MoreInputsPageProps> = (
  props: MoreInputsPageProps
) => {
  const isMetaDataReceived = useAppSelector(
    (state) => state.insurance.isMetaDataReceived
  );
  const insurancePackages = useAppSelector((state) =>
    state.insurance.packages.filter((item: Package) => !item.isPlusPackage)
  );
  const offerInfo = useAppSelector((state) => state.insurance.offer);

  const { policyNumber: policyNumberFromUrl } = InsuranceHelper.getPolicyAndChannelFromUrl();
  const policyDetails = InsuranceHelper.retrievePolicyInfoFromURL();
  const dispatch = useAppDispatch();
  const propertyDataSuccessImage =
    fetchGenericIllustrations(
      'QuestionMarkErrorModalNCI',
      IllustrationType.SIMPLE,
      [
        {
          name: 'QuestionMarkErrorModalNCI',
          filename: 'QuestionMarkErrorModal_NCI',
          fileType: 'svg'
        }
      ]
    ) || '';
  useEffect(() => {
    InsuranceHelper.setPageTitle(
      TextHelper.getPageTitle(props.product, ROUTES.moreInputsPage)
    );
  }, []);

  if(policyNumberFromUrl) {
    sessionStorage.setItem(
      SESSION_KEYS.FOLLOW_UP_OFFER,
      policyNumberFromUrl
    );
  }

  useEffect(() => {
    if (isMetaDataReceived) {
      const stepStatus = TextHelper.getHeaderStepLabels(
        props.product,
        ROUTES.moreInputsPage
      );
      dispatch(updatePurchaseStatus(stepStatus));
      
      if (!insurancePackages.length) {
        const policyNumber = policyNumberFromUrl || policyDetails.policyNumber;
        if (policyNumber) {
            dispatch(
              apiCallBegan({
                init: {
                  headers: {
                    Applikasjon: APP_CONSTANTS.application
                  }
                },
                url: policyNumberFromUrl ? API_ENDPOINTS.getSavedOfferURL(policyNumber) : API_ENDPOINTS.getOfferURL(policyNumber),
                apiOperation: policyNumberFromUrl ? API_OPERATIONS.RESUME_OFFER : API_OPERATIONS.GET_OFFER,
                method: APICallMethods.GET_JSON,
                onStart: onApiBegan.type,
                onSuccess: onApiSuccess.type,
                onError: onApiError.type
              })
            );
        } else {
          dispatch(updateLoadingStatus('succeeded'));
        }
      }
      
      logMessage(
        `Meta data received and loading the more-inputs page for ${props.product}`,
        {severity: 'INFO'}
      )
      trackMoreInputsPage();
    }
  }, [isMetaDataReceived]);
  const trackMoreInputsPage = () => {
    const scenarioStep = 'details';
    const policyNumber = policyNumberFromUrl || policyDetails.policyNumber || offerInfo?.policyNumber;
    const trackOptions = {
      event_name: 'product_view',
      product_name: TrackingHelper.getProductListToTrack(props.product),
      product_brand: ['insurance'],
      product_sku: [TrackingHelper.getProductNumberToTrack(props.product)],
      product_category: [
        TrackingHelper.getProductCategoryToTrack(props.product)
      ],
      scenario_start: '1',
      scenario_name: TrackingHelper.getScenarioNameFromProduct(props.product),
      scenario_step: scenarioStep,
      scenario_type: TrackingHelper.isFollowUpOffer(policyNumber) ? 'follow-up-offer' : ''
    };
    tracker.setOptions(trackOptions);
    tracker.trackScenerioStep(
      'offer',
      TrackingHelper.getScenarioNameFromProduct(props.product),
      scenarioStep
    );
    tracker.setOptions({ event_name: '', scenario_start: '' });
  };
  const propertyInfo = InsuranceHelper.getPropertyInfo(props.product);
  return (
    <PageLoader>
      <ErrorPageHandler>
        {propertyInfo.isPresent && propertyDataSuccessImage && (
          <PropertyDataModal
            imageSrc={propertyDataSuccessImage}
            message={propertyInfo.propertyDataMessage}
            btnActionLabel={AppConstants.getAppText(
              'webshop.villa.propertyData.modalBtnLabel'
            )}
          />
        )}
        <HeaderContainer currentPage={ROUTES.moreInputsPage} productName={props.product} />
        <div className="pb-md-5 pt-md-3 pb-3">
          <PriceBanner
            wrapperClassName="total-price-wrapper"
            shouldStickToTop={true}
            centerTitle={TextHelper.getMoreInfoBannerText(props.product)}
          />
          <main>
            <SectionLoader></SectionLoader>
            <Row className="more-inputs-contanier mt-6">
              <Col className="more-inputs-wrapper pb-3">
                  <MoreInputsContainer product={props.product} />
              </Col>
            </Row>
          </main>
          {policyNumberFromUrl && (
            <WelcomeBackModal
              welcomeMessage={{
                header: 'Kolla dina uppgifter',
                message: 'Vad roligt att du vill teckna en försäkring! \n Vi har hämtat en del information om ditt boende från Rikshem. Kontrollera att allting stämmer och fyll på med det som saknas.'
              }}
              primaryBtnLabel="Jag förstår"
              closeLabel="Stäng information"
              modalLabel="Hämtade uppgifter"
            />
          )}
        </div>
      </ErrorPageHandler>
    </PageLoader>
  );
};
